import * as React from 'react'
import { Loader2, Star, Trash2, XCircle } from 'lucide-react'

import { deriveAdStage } from '@/pages/Jobs'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { formatDate, truncate } from '@/lib/utils'
import type { GenerationJob } from '@/types'

interface Props {
  job: GenerationJob
  onSelect?: (job: GenerationJob) => void
  onCancel?: (jobId: number) => void
  onDelete?: (jobId: number) => void
  onStar?: (jobId: number, currentlyStarred: boolean) => Promise<void>
}

export function JobCard({ job, onSelect, onCancel, onDelete, onStar }: Props) {
  const active = job.status === 'queued' || job.status === 'processing'
  const deletable = !active

  const stage = deriveAdStage(job.status)

  // Optimistic star state
  const [starred, setStarred] = React.useState(job.is_starred)
  const [starPending, setStarPending] = React.useState(false)

  // Keep in sync if parent list refreshes
  React.useEffect(() => {
    setStarred(job.is_starred)
  }, [job.is_starred])

  const handleStar = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!onStar || starPending) return
    const prev = starred
    setStarred(!prev)
    setStarPending(true)
    try {
      await onStar(job.id, prev)
    } catch {
      setStarred(prev)
    } finally {
      setStarPending(false)
    }
  }

  return (
    <Card
      onClick={() => onSelect?.(job)}
      className="cursor-pointer transition-colors hover:border-foreground/30"
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs text-muted-foreground">
            #{job.id} · {formatDate(job.created_at)}
          </div>
          <div className="flex items-center gap-1.5">
            {onStar && (
              <Button
                variant="ghost"
                size="icon"
                className={`h-6 w-6 ${starred ? 'text-yellow-400 hover:text-yellow-300' : 'text-muted-foreground hover:text-yellow-400'}`}
                title={starred ? 'Unstar job' : 'Star job'}
                disabled={starPending}
                onClick={handleStar}
                aria-label={starred ? 'Unstar job' : 'Star job'}
              >
                <Star className="h-3.5 w-3.5" fill={starred ? 'currentColor' : 'none'} />
              </Button>
            )}
            <Badge
              variant={stage.variant}
              className={`capitalize ${stage.className ?? ''}`}
            >
              {stage.label}
            </Badge>
            {active && onCancel && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-muted-foreground hover:text-destructive"
                title="Cancel job"
                onClick={(e) => {
                  e.stopPropagation()
                  onCancel(job.id)
                }}
              >
                <XCircle className="h-3.5 w-3.5" />
              </Button>
            )}
            {deletable && onDelete && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-muted-foreground hover:text-destructive"
                title="Delete job"
                onClick={(e) => {
                  e.stopPropagation()
                  onDelete(job.id)
                }}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>

        <p className="text-sm leading-snug">{truncate(job.prompt_text, 160)}</p>

        <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
          <Badge variant="outline">{job.aspect_ratio}</Badge>
          <Badge variant="outline" className="uppercase text-[10px]">{job.output_type}</Badge>
          <Badge variant="outline">{job.duration_seconds}s</Badge>
          <span className="truncate">{job.style_profile.replace(/_/g, ' ')}</span>
          {job.cost_cents != null && job.cost_cents > 0 && (
            <span className="text-[11px] text-muted-foreground">${(job.cost_cents / 100).toFixed(2)}</span>
          )}
          {job.generation_time_seconds != null && (
            <span className="text-[11px] text-muted-foreground">{Math.round(job.generation_time_seconds)}s gen</span>
          )}
          {active && (
            <span className="inline-flex items-center gap-1 text-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              {job.status}
            </span>
          )}
          {job.status === 'failed' && job.error_message && (
            <span className="text-destructive truncate max-w-full">{job.error_message}</span>
          )}
        </div>

        {job.status === 'completed' && job.output_url && (
          job.output_type === 'image' ? (
            <img
              src={job.output_url}
              alt="Generated"
              className="w-full max-h-72 object-contain rounded-md border border-border"
              loading="lazy"
            />
          ) : (
            <video
              src={job.output_url}
              className="w-full max-h-72 rounded-md border border-border bg-black"
              controls
              preload="metadata"
              playsInline
            />
          )
        )}
      </CardContent>
    </Card>
  )
}
